import React, { useState, useEffect } from 'react';
import {
  Container, TextField, Button, Box, Dialog, DialogTitle, DialogContent,
  DialogActions, IconButton, Typography, Chip, Tooltip, useMediaQuery, useTheme,
  Stack, Paper
} from '@mui/material';
import { Close as CloseIcon, Visibility, CalendarToday, Send, Link as LinkIcon, Terminal, Place, Person, BugReport } from '@mui/icons-material';
import TituloPagina from './utils/TituloPagina.jsx';
import { MenuInicial } from "./MenuInicial";
import { useNavigate } from "react-router-dom";
import { API_BASE_URL, ERRORESLIST } from '../services/apiConfig';
import { DataGrid } from '@mui/x-data-grid';
import Cargando from './utils/Cargando.jsx';
import axios from 'axios';

import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import './DatePickerStyles.css';

const authHeader = () => {
  const t = sessionStorage.getItem('authToken');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const codeBlock = (text, maxH = 200) => {
  let formatted = text;
  try { formatted = JSON.stringify(JSON.parse(text), null, 2); } catch {}
  return (
    <Box sx={{
      bgcolor: '#0d1117', color: '#e6edf3', p: 1.5, borderRadius: 1.5, mt: 0.5,
      fontFamily: 'JetBrains Mono, Consolas, monospace', fontSize: 12,
      whiteSpace: 'pre-wrap', wordBreak: 'break-all',
      maxHeight: maxH, overflow: 'auto',
      border: '1px solid #30363d',
      boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.03)'
    }}>{formatted}</Box>
  );
};

const chipColor = (status) => {
  if (!status || status === 'network') return 'default';
  if (status >= 500) return 'error';
  if (status >= 400) return 'warning';
  if (status >= 300) return 'info';
  return 'success';
};

function ErrorDetailDialog({ open, error, onClose }) {
  if (!error) return null;

  let parsed = null;
  try { parsed = JSON.parse(error.error); } catch { parsed = null; }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        borderBottom: '1px solid', borderColor: 'divider', pb: 1.5
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{
            width: 10, height: 10, borderRadius: '50%',
            bgcolor: parsed?.status >= 500 ? 'error.main' : parsed?.status >= 400 ? 'warning.main' : 'grey.400'
          }} />
          <span style={{ fontWeight: 600 }}>Error #{error.id}</span>
          {parsed?.status && <Chip label={parsed.status} size="small" color={chipColor(parsed.status)} />}
          {parsed?.metodo && <Chip label={parsed.metodo} size="small" color={parsed.metodo === 'GET' ? 'info' : 'warning'} />}
        </Box>
        <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: 2.5, pb: 1 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>

          {/* Endpoint + ruta */}
          <Box sx={{
            bgcolor: 'action.hover', borderRadius: 1.5, p: 1.5,
            border: '1px solid', borderColor: 'divider'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <Typography variant="caption" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, color: 'text.secondary' }}>Endpoint</Typography>
              {parsed?.ruta && <Chip label={parsed.ruta} size="small" variant="outlined" sx={{ fontSize: 11 }} />}
            </Box>
            <Typography variant="body2" sx={{ fontFamily: 'JetBrains Mono, Consolas, monospace', fontSize: 13, wordBreak: 'break-all' }}>
              {error.aplicacion}
            </Typography>
          </Box>

          {/* Metadata chips */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            <Chip icon={<CalendarToday sx={{ fontSize: 14 }} />} label={error.fecha} size="small" variant="outlined" />
            <Chip icon={<Place sx={{ fontSize: 14 }} />} label={`IP: ${error.ip}`} size="small" variant="outlined" />
            <Chip icon={<Person sx={{ fontSize: 14 }} />} label={error.username || error.usuarioid || '—'} size="small" variant="outlined" />
          </Box>

          {/* Payload */}
          {parsed?.payload && (
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <Send sx={{ fontSize: 16, color: 'text.secondary' }} />
                <Typography variant="caption" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, color: 'text.secondary' }}>Payload</Typography>
              </Box>
              {codeBlock(parsed.payload, 180)}
            </Box>
          )}

          {/* Query Params */}
          {parsed?.params && (
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <LinkIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                <Typography variant="caption" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, color: 'text.secondary' }}>Query Params</Typography>
              </Box>
              {codeBlock(parsed.params, 150)}
            </Box>
          )}

          {/* Respuesta */}
          {parsed?.respuesta && (
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <Terminal sx={{ fontSize: 16, color: 'text.secondary' }} />
                <Typography variant="caption" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, color: 'text.secondary' }}>Respuesta</Typography>
              </Box>
              {codeBlock(parsed.respuesta, 250)}
            </Box>
          )}

          {/* Raw fallback */}
          {!parsed && (
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <Typography variant="caption" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, color: 'text.secondary' }}>Error completo</Typography>
              </Box>
              {codeBlock(error.error, 400)}
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ borderTop: '1px solid', borderColor: 'divider', pt: 1 }}>
        <Button onClick={onClose} variant="contained" disableElevation>Cerrar</Button>
      </DialogActions>
    </Dialog>
  );
}

function ErroresList() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [loading, setLoading] = useState(true);
  const userData = JSON.parse(sessionStorage.getItem("userData"));
  const sucursalid = userData?.sucursalid;
  const [users, setUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [startDate, setStartDate] = useState(new Date());
  const [endDate, setEndDate] = useState(new Date());
  const [selectedError, setSelectedError] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const handleSearchChange = (e) => {
    const term = e.target.value;
    setSearchTerm(term);

    const filteredData = users.filter((user) =>
      user.error.toLowerCase().includes(term.toLowerCase())
    );
    setFilteredUsers(filteredData);
  };

  const handleStartDate = (date) => {
    setStartDate(date);
  };

  const handleEndDate = (date) => {
    setEndDate(date);
  };

  useEffect(() => {
    handleUpdateData();
  }, []);

  const handleUpdateData = () => {
    setLoading(true);

    const fechainicio = startDate;
    const date = new Date(fechainicio);

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');

    const fechainiciod = `${year}-${month}-${day}`;

    const fechafin = endDate;
    const datef = new Date(fechafin);

    const yearf = datef.getFullYear();
    const monthf = String(datef.getMonth() + 1).padStart(2, '0');
    const dayf = String(datef.getDate()).padStart(2, '0');

    const fechainiciof = `${yearf}-${monthf}-${dayf}`;

    axios.get(`${API_BASE_URL}${ERRORESLIST}?startDate=${fechainiciod}&endDate=${fechainiciof}`, { headers: authHeader() })
      .then((response) => {
        const payload = response.data?.data ?? response.data;
        const userData = Array.isArray(payload) ? payload : [];
        setUsers(userData);
        const filteredData = userData.filter(user => (user.error || '').toLowerCase().includes(searchTerm.toLowerCase()));
        setFilteredUsers(filteredData);
        setLoading(false);
      })
      .catch((error) => {
        console.error('Error al cargar los errores', error);
        setLoading(false);
      });
  };

  const handleVerMas = (row) => {
    setSelectedError(row);
    setDialogOpen(true);
  };

  const columns = [
    { field: 'id', headerName: 'ID', width: 70 },
    { field: 'fecha', headerName: 'Fecha', width: 155 },
    { field: 'username', headerName: 'Usuario', width: 150 },
    { field: 'aplicacion', headerName: 'Endpoint', width: 250, renderCell: (params) => (
      <span style={{ fontSize: 12, wordBreak: 'break-word' }}>{params.value}</span>
    )},
    { field: 'error', headerName: 'Error', width: 350, renderCell: (params) => {
      let preview = params.value;
      try {
        const parsed = JSON.parse(params.value);
        preview = `[${parsed.status || '?'}] ${parsed.mensaje || ''}`;
      } catch {}
      return <span style={{ fontSize: 12, wordBreak: 'break-word' }}>{preview}</span>;
    }},
    { field: 'ip', headerName: 'IP', width: 140 },
    {
      field: 'accion', headerName: '', width: 70, sortable: false,
      renderCell: (params) => (
        <Tooltip title="Ver detalle">
          <IconButton size="small" color="primary" onClick={() => handleVerMas(params.row)}>
            <Visibility />
          </IconButton>
        </Tooltip>
      )
    },
  ];

  return (
    <Container maxWidth={isMobile ? false : "xl"} sx={{ px: isMobile ? 1 : 3 }}>
      <MenuInicial />
      <Box sx={{ my: 3 }}>
        <TituloPagina
          icon={BugReport}
          titulo="Errores"
          ayuda="Errores HTTP capturados por el frontend"
        />
        <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
          {isMobile ? (
            <>
              <DatePicker
                selected={startDate}
                onChange={(date) => setStartDate(date)}
                dateFormat="dd/MM/yyyy"
                customInput={
                  <TextField size="small" fullWidth InputProps={{
                    startAdornment: <CalendarToday sx={{ mr: 1, fontSize: '1rem', color: 'text.secondary' }} />
                  }} />
                }
              />
              <DatePicker
                selected={endDate}
                onChange={(date) => setEndDate(date)}
                dateFormat="dd/MM/yyyy"
                customInput={
                  <TextField size="small" fullWidth InputProps={{
                    startAdornment: <CalendarToday sx={{ mr: 1, fontSize: '1rem', color: 'text.secondary' }} />
                  }} />
                }
              />
            </>
          ) : (
            <>
              <DatePicker
                selected={startDate}
                onChange={(date) => setStartDate(date)}
                selectsStart
                startDate={startDate}
                endDate={endDate}
                dateFormat="dd/MM/yyyy"
                customInput={<TextField label="Fecha Desde" size="small" />}
              />
              <DatePicker
                selected={endDate}
                onChange={(date) => setEndDate(date)}
                selectsEnd
                startDate={startDate}
                endDate={endDate}
                minDate={startDate}
                dateFormat="dd/MM/yyyy"
                customInput={<TextField label="Fecha Hasta" size="small" />}
              />
            </>
          )}
          <Button onClick={handleUpdateData} variant="contained">
            Buscar
          </Button>
        </Box>
        <TextField
          label="Buscar por Error"
          value={searchTerm}
          onChange={handleSearchChange}
          fullWidth
          sx={{ mb: 2 }}
        />
        {loading ? (
          <Cargando></Cargando>
        ) : isMobile ? (
          <Stack spacing={1}>
            {filteredUsers.length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>Sin resultados.</Typography>
            )}
            {filteredUsers.map((row) => {
              let preview = row.error;
              let status = null;
              try {
                const parsed = JSON.parse(row.error);
                preview = parsed.mensaje || row.error;
                status = parsed.status;
              } catch {}
              return (
                <Paper key={row.id} variant="outlined" onClick={() => handleVerMas(row)} sx={{ p: 1.2, cursor: 'pointer' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                        {status && <Chip label={status} size="small" color={chipColor(status)} sx={{ height: 18 }} />}
                        <Typography variant="body2" sx={{ fontWeight: 600, wordBreak: 'break-all' }}>{row.aplicacion}</Typography>
                      </Box>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', wordBreak: 'break-word' }}>{preview}</Typography>
                    </Box>
                    <IconButton size="small" color="primary" onClick={(e) => { e.stopPropagation(); handleVerMas(row); }}><Visibility fontSize="small" /></IconButton>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1, mt: 0.75, flexWrap: 'wrap' }}>
                    <Chip size="small" icon={<CalendarToday sx={{ fontSize: 13 }} />} label={row.fecha} variant="outlined" />
                    <Chip size="small" icon={<Person sx={{ fontSize: 13 }} />} label={row.username || row.usuarioid || '—'} variant="outlined" />
                  </Box>
                </Paper>
              );
            })}
          </Stack>
        ) : (
          <div style={{ height: 600, width: '100%' }}>
            <DataGrid rows={filteredUsers} columns={columns} pageSizeOptions={[25, 50, 100]}
              initialState={{ sorting: { sortModel: [{ field: 'fecha', sort: 'desc' }] } }} />
          </div>
        )}
      </Box>

      <ErrorDetailDialog
        open={dialogOpen}
        error={selectedError}
        onClose={() => setDialogOpen(false)}
      />
    </Container>
  );
}

export default ErroresList;
